import React, { useState } from "react";
import {
  Box,
  Flex,
  Text,
  Image,
  Badge,
  Skeleton,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerCloseButton,
} from "@chakra-ui/react";
import { bottomDrawerSize } from "./drawerSizing";

// The hero image in a fixed-height slot: sized before the file arrives, so
// the text below it doesn't jump down mid-open when the image loads. A
// skeleton holds the slot and the image fades in over it; on error the slot
// collapses rather than showing a broken image.
const SectionImage = ({ src, alt }) => {
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);

  if (failed) return null;

  return (
    <Box
      position="relative"
      h={{ base: "200px", md: "280px" }}
      borderRadius="12px"
      overflow="hidden"
      mb={4}
    >
      {!loaded && <Skeleton position="absolute" inset={0} h="100%" />}
      <Image
        src={src}
        alt={alt}
        w="full"
        h="100%"
        objectFit="cover"
        opacity={loaded ? 1 : 0}
        transition="opacity 0.25s ease"
        onLoad={() => setLoaded(true)}
        onError={() => setFailed(true)}
      />
    </Box>
  );
};

// Drawer for a single host-authored property guide section. Matches
// `GuideDrawer` (bottom sheet, same header, same sizing) so every guide in the
// hub opens the same way; only the body differs.
//
// Property sections hold rich HTML from the dashboard's Quill editor, so the
// body renders through `.ql-editor` with explicit color overrides — the editor
// emits inline colors tuned for a light canvas, which are unreadable on the
// app's dark surface without them. API guides use `GuideDrawer` instead; see
// `Logic/propertyGuideItems.js` for why the two can't share one renderer.
export const PropertySectionDrawer = ({ isOpen, onClose, section }) => (
  <Drawer placement="bottom" onClose={onClose} isOpen={isOpen} size="full">
    <DrawerOverlay />
    <DrawerContent
      bg="#1A202C"
      color="white"
      {...bottomDrawerSize({ mobile: 80, desktop: 88 })}
      borderTopRadius="20px"
      display="flex"
      flexDirection="column"
      maxW={{ base: "100%", md: "720px" }}
      mx="auto"
      mb={{ md: 8 }}
      borderRadius={{ md: "24px" }}
    >
      <DrawerHeader
        px={{ base: 4, md: 6 }}
        py={4}
        borderBottom="1px solid"
        borderColor="rgba(255, 255, 255, 0.1)"
        flexShrink={0}
      >
        <Flex alignItems="center" justifyContent="space-between">
          <Flex direction="column">
            <Text fontSize={{ base: "16px", md: "18px" }} fontWeight="700" lineHeight="1.2">
              {section?.title || "Property Information"}
            </Text>
            {section?.catalog?.name && (
              <Badge
                colorScheme="purple"
                variant="subtle"
                fontSize="10px"
                px={2}
                py={1}
                mt={1}
                alignSelf="flex-start"
              >
                {section.catalog.name}
              </Badge>
            )}
          </Flex>
          <DrawerCloseButton pos="relative" top={0} right={0} />
        </Flex>
      </DrawerHeader>

      <DrawerBody px={{ base: 4, md: 6 }} pt={{ base: 4, md: 5 }} pb={6} overflowY="auto" flex={1} minH={0} overscrollBehavior="contain">
        {section?.images?.[0] && (
          <SectionImage
            // Remount per image so a reused drawer never shows the previous
            // section's picture while the new one loads.
            key={section.images[0]}
            src={section.images[0]}
            alt={section?.title || "Property"}
          />
        )}
        <Box
          className="ql-editor"
          fontSize="14px"
          lineHeight="1.6"
          color="gray.200"
          whiteSpace="pre-wrap"
          // Quill's own padding would double up with the drawer's.
          p={0}
          dangerouslySetInnerHTML={{
            __html: section?.html || section?.title || "",
          }}
          sx={{
            "& *": {
              color: "inherit !important",
            },
            "& img": {
              maxWidth: "100%",
              height: "auto",
              borderRadius: "8px",
              margin: "8px 0",
            },
            "& p": {
              marginBottom: "8px",
              lineHeight: "1.6",
              color: "gray.200 !important",
            },
            "& h1, & h2, & h3, & h4, & h5, & h6": {
              color: "white !important",
              fontWeight: "700",
            },
            "& strong": {
              fontWeight: "700",
              color: "white !important",
            },
            "& span": {
              color: "gray.200 !important",
            },
            "& div": {
              color: "gray.200 !important",
            },
            "& li": {
              color: "gray.200 !important",
            },
            "& ul, & ol": {
              color: "gray.200 !important",
            },
            "& a": {
              color: "#817EFF !important",
              textDecoration: "underline !important",
            },
            "& a:hover": {
              color: "#615edc !important",
            },
            "& a:visited": {
              color: "#817EFF !important",
            },
            "& a:active": {
              color: "#615edc !important",
            },
          }}
        />
      </DrawerBody>
    </DrawerContent>
  </Drawer>
);

export default PropertySectionDrawer;
